import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { money, when } from "@/lib/rides";
import { PAY_LABEL } from "@/components/booking/CheckoutPanel";
import { MessagesButton } from "@/components/RideMessages";
import { ShareRideButton } from "@/components/FollowRide";
import { StatusBadge, Stops } from "@/components/RideStatus";
import { Button } from "@/components/ui/button";

function RiderActions({ ride }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const run = async (path, msg) => {
    setBusy(true);
    try {
      await api.post(`/rides/${ride.id}/${path}`, {});
      toast.success(msg);
      qc.invalidateQueries({ queryKey: ["myRides"] });
      qc.invalidateQueries({ queryKey: ["activeRide"] });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  const cancellable = ["pending", "confirmed", "assigned"].includes(ride.status);
  if (!cancellable) return null;
  return (
    <div className="mt-3 flex gap-2">
      {cancellable && (
        <Button size="sm" variant="outline" className="rounded-full" disabled={busy} onClick={() => run("cancel", "Ride cancelled")} data-testid="history-cancel-ride-button">
          Cancel
        </Button>
      )}
    </div>
  );
}

export function RideHistory({ perspective }) {
  const { data, isLoading } = useQuery({
    queryKey: ["myRides", perspective],
    queryFn: () => api.get(perspective === "driver" ? "/driver/history" : "/rides").then((r) => r.data),
    refetchInterval: 15000,
  });
  if (isLoading) return <Loader2 className="h-5 w-5 animate-spin text-primary" />;
  const completed = (data || []).filter((r) => r.status === "completed");
  const total = completed.reduce((s, r) => s + (r.fare || 0), 0);

  return (
    <div className="space-y-3" data-testid="ride-history">
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-2xl bg-muted p-4">
          <p className="text-xs text-muted-foreground">Completed trips</p>
          <p className="font-display text-2xl font-bold" data-testid="history-completed-count">{completed.length}</p>
        </div>
        <div className="rounded-2xl bg-muted p-4">
          <p className="text-xs text-muted-foreground">{perspective === "driver" ? "Fares earned" : "Total spent"}</p>
          <p className="font-display text-2xl font-bold" data-testid="history-total">{money(total)}</p>
        </div>
      </div>
      {!data?.length && <p className="py-6 text-sm text-muted-foreground">No rides yet.</p>}
      {data?.map((r) => (
        <div key={r.id} className="rounded-2xl border bg-card p-4" data-testid="history-ride-item">
          <div className="mb-2 flex items-center justify-between">
            <StatusBadge status={r.status} />
            <span className="font-semibold" data-testid="history-ride-fare">{money(r.fare)}</span>
          </div>
          <Stops ride={r} />
          {perspective === "rider" && ["pending", "confirmed", "assigned"].includes(r.status) && (
            <div className="mt-2 flex flex-wrap gap-2">
              <MessagesButton rideId={r.id} className="h-8 text-xs" />
              {r.status !== "pending" && <ShareRideButton rideId={r.id} />}
            </div>
          )}
          <p className="mt-2 text-xs text-muted-foreground">
            Booked {when(r.created_at)}{r.distance_miles != null ? ` · ${r.distance_miles} mi` : ""}
            {r.quote?.recurring?.applied ? ` · ${r.quote.recurring.pct}% recurring discount` : ""}
            {r.discount?.code ? ` · ${r.discount.code} -${money(r.discount.amount)}` : ""}
            {r.payment_method ? ` · ${PAY_LABEL[r.payment_method]}` : ""}
            {r.credit_applied > 0 ? ` · credit used ${money(r.credit_applied)}` : r.credit_planned > 0 ? ` · credit reserved ${money(r.credit_planned)}` : ""}
            {r.amount_due != null && r.status !== "cancelled" && r.status !== "declined" ? ` · due ${money(r.amount_due)}` : ""}
            {perspective === "rider" && r.driver_name ? ` · ${r.driver_name}` : ""}
            {perspective === "driver" ? ` · ${r.rider_name}` : ""}
          </p>
          {r.decline_reason && <p className="mt-1 text-xs text-amber-300">Declined: {r.decline_reason}</p>}
          {r.cancellation_reason && <p className="mt-1 text-xs text-muted-foreground">Cancelled: {r.cancellation_reason}</p>}
          {r.pricing_review && r.status === "pending" && <p className="mt-1 text-xs text-amber-300">Pricing review required</p>}
          {perspective === "rider" && <RiderActions ride={r} />}
        </div>
      ))}
    </div>
  );
}
